/**
 * <yoshien-header-menu>
 *   <details>
 *     <summary data-ref="trigger" aria-label="Menu"></summary>
 *     <div data-ref="content"></div>
 *   </details>
 * </yoshien-header-menu>
 *
 * - アクセシブルなメニューを作成するためのカスタム要素
 * - <details>要素を使用して、メニューのトグルを実現
 * - <summary>要素を使用して、メニューのトリガーを実現
 * - data-ref属性を使用して、要素を参照する
 * - aria-label属性を使用して、開閉状態を示す
 * - フォーカストラップを実装して、メニュー内の要素にフォーカスを移動
 * - キーボード操作をサポートする
 * - メニューを閉じる際は、まずは`data-state`を`closed`にし、animationendまたは transitionendイベントを使用して`open`を削除
 */

class YoshienHeaderMenu extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  wrapper;
  /** @type {HTMLElement} */
  trigger;
  /** @type {HTMLDivElement} */
  content;

  /** @type { boolean } */
  isOpen = false;

  constructor() {
    super();
    const randomId = Math.random().toString(36).substring(2, 10);
    this.wrapper = this.querySelector("details");
    this.trigger = this.querySelector("[data-ref='trigger']");
    this.content = this.querySelector("[data-ref='content']");

    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.setAttribute("aria-haspopup", "true");
    this.trigger.setAttribute("aria-controls", `menu-${randomId}`);
    this.content.setAttribute("id", `menu-${randomId}`);
    this.content.setAttribute("role", "menu");
    this.content.setAttribute("aria-hidden", "true");
    this.wrapper.setAttribute("data-state", "closed");
  }

  toggle(open) {
    this.isOpen = open;
    this.trigger.setAttribute("aria-expanded", String(open));
    this.trigger.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    this.content.setAttribute("aria-hidden", String(!open));
    if (open) {
      this.wrapper.setAttribute("open", "");
      this.wrapper.setAttribute("data-state", "open");
      window.addEventListener("keydown", this.handleKeydown.bind(this));
    } else {
      this.wrapper.setAttribute("data-state", "closed");
      const hasTransitionOrAnimation =
        window.getComputedStyle(this.content).transitionDuration !== "0s" ||
        window.getComputedStyle(this.content).animationDuration !== "0s";
      if (hasTransitionOrAnimation) {
        const handleClose = (e) => {
          console.log(e);
          this.wrapper.removeAttribute("open");
          this.content.removeEventListener("animationend", handleClose);
          this.content.removeEventListener("transitionend", handleClose);
        };
        this.content.addEventListener("animationend", handleClose);
        this.content.addEventListener("transitionend", handleClose);
      } else {
        this.wrapper.removeAttribute("open");
      }
      window.removeEventListener("keydown", this.handleKeydown.bind(this));
    }
  }

  toggleFocusTrap() {
    const focusableElements = this.content.querySelectorAll(
      "a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])",
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (this.isOpen) {
      firstElement.focus();
      this.content.addEventListener("keydown", (event) => {
        if (event.key === "Tab") {
          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }
      });
    } else {
      document.activeElement.blur();
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.toggle(false);
      this.toggleFocusTrap();
    }
  }

  /**
   * @param { MouseEvent | KeyboardEvent } event
   */
  handleToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "click" || event.key === "Enter") {
      this.toggle(!this.isOpen);
      this.toggleFocusTrap();
    }
  }

  connectedCallback() {
    this.trigger.addEventListener("click", this.handleToggle.bind(this));
    this.trigger.addEventListener("keydown", this.handleToggle.bind(this));
  }

  disconnectedCallback() {
    this.trigger.removeEventListener("click", this.handleToggle.bind(this));
    this.trigger.removeEventListener("keydown", this.handleToggle.bind(this));
  }
}

window.customElements.define("yoshien-header-menu", YoshienHeaderMenu);
